import Delete from "@mui/icons-material/Delete";
import { type GridRowSelectionModel } from "@mui/x-data-grid-pro";
import axios, { type AxiosError } from "axios";
import { type ChangeEvent, useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";

import apiRoutes from "../../../constants/api-routes";
import messageLevels from "../../../constants/message-levels";
import messages from "../../../constants/messages";
import navigationItemReferences from "../../../constants/navigation-item-references";
import useAuthHeader from "../../../hooks/useAuthHeader";
import useCoAppNavigation from "../../../hooks/useCoAppNavigation";
import useDatagridSorting from "../../../hooks/useDatagridSorting";
import {
	emptyGridRowSelectionModel, gridRowSelectionIdsArray,
	gridRowSelectionSize, resolveGridSelectedRowIds
} from "../../../helpers/dataGridRowSelectionModel";
import useToastAlert from "../../../hooks/useToastAlert";
import { selectOrganizationIdentityProvider, selectOrganizationSsoAndScim } from "../../../redux/userSlice";
import CoAppDatagridHeader from "../../global/components/datagrid/CoAppDatagridHeader";
import CoAppStandardDataGrid from "../../global/components/datagrid/CoAppStandardDataGrid";
import useCoAppDataGridFilters from "../../global/components/datagrid/useCoAppDataGridFilters";
import CoAppCreateGroupOrRoleModal from "../../global/components/modals/CoAppCreateGroupOrRoleModal";
import CoAppDestructiveConfirmationModal from "../../global/components/modals/CoAppDestructiveConfirmationModal";
import type { Group } from "../../global/types";
import SettingsDataGridSelectionRow, { destructiveIconSx } from "../SettingsDataGridSelectionRow";
import GroupManagementColumns from "./columns/GroupManagementColumns";

export default function GroupManagement() {
	const organizationSsoAndScim = useSelector(selectOrganizationSsoAndScim);
	const authHeader = useAuthHeader();
	const { handleToastAlert } = useToastAlert();
	const { sortModel, handleSortModelChange, handleInitializeSortModel } = useDatagridSorting("groups");
	const { openResourceEditPage } = useCoAppNavigation();
	const identityProviderName = useSelector(selectOrganizationIdentityProvider);

	const [groupSeed, setGroupSeed] = useState(0);
	const [organizationGroups, setOrganizationGroups] = useState<Group[]>([]);
	const [groupCreateDialogOpen, setGroupCreateDialogOpen] = useState(false);
	const [groupDeleteDialogOpen, setGroupDeleteDialogOpen] = useState(false);
	const [groupBeingDeleted, setGroupBeingDeleted] = useState<Group | null>(null);
	const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
	const [groupNameError, setGroupNameError] = useState("");
	const [groupName, setGroupName] = useState("");
	const [selectedGroups, setSelectedGroups] = useState<GridRowSelectionModel>(() => emptyGridRowSelectionModel());
	const [loading, setLoading] = useState(true);

	const handleToggleGroupDeleteDialog = useCallback((group: Group | null) => {
		setGroupDeleteDialogOpen(prev => !prev);
		setGroupBeingDeleted(group);
	}, []);

	const duplicateGroup = useCallback((groupId: string) => {
		axios
			.post(apiRoutes.copyGroup(groupId), {}, { headers: authHeader })
			.then(() => {
				setGroupSeed(Math.random());
			})
			.catch((err: AxiosError) => {
				console.error(err);
				handleToastAlert(messageLevels.ERROR, messages.GROUP_DUPLICATION_ERROR_MSG);
			});
	}, [authHeader, handleToastAlert]);

	// Stable column defs — new arrays on each render make MUI X discard autosized widths.
	const columns = useMemo(
		() =>
			GroupManagementColumns({
				openResourceEditPage,
				navigationItemReferences,
				duplicateGroup,
				handleToggleGroupDeleteDialog,
				identityProviderName,
				organizationSsoAndScim: organizationSsoAndScim || false,
			}),
		[identityProviderName, organizationSsoAndScim]
	);
	

	const createNewGroup = () => {
		if (!groupName) {
			setGroupNameError("Required *");
			return;
		}

		const groupJSON = {
			"groupName": groupName,
		};

		axios.post(apiRoutes.createGroup, groupJSON, { headers: authHeader })
			.then(() => {
				setGroupSeed(Math.random());
				setGroupCreateDialogOpen(false);
				setGroupName("");
				setGroupNameError("");
			})
			.catch((err: AxiosError) => {
				if (err.response?.data && typeof err.response.data === "object" && "message" in err.response.data && 
					typeof err.response.data.message === "string" && err.response.data.message.includes("already exists")) {
					setGroupNameError(messages.RESOURCE_ALREADY_EXISTS_ERROR_MSG("Group"));
					return;
				}
				handleToastAlert(messageLevels.ERROR, messages.GROUP_CREATION_ERROR_MSG);
			});
	};

	const handleToggleGroupCreateDialog = () => {
		setGroupCreateDialogOpen(!groupCreateDialogOpen);
		setGroupName("");
		setGroupNameError("");
	};

	const handleGroupNameChange = (e: ChangeEvent<HTMLInputElement>) => {
		setGroupName(e.target.value);
		setGroupNameError("");
	};

	const deleteGroup = () => {
		if (!groupBeingDeleted?.id) {
			return;
		}
		axios
			.delete(apiRoutes.deleteGroup(groupBeingDeleted.id), {
				headers: authHeader,
			})
			.then(() => {
				setGroupSeed(Math.random());
				handleToggleGroupDeleteDialog(null);
			})
			.catch((err: AxiosError) => {
				console.error(err);
				handleToastAlert(messageLevels.ERROR, messages.GROUP_DELETION_ERROR_MSG);
				handleToggleGroupDeleteDialog(null);
			});
	};

	const bulkDeleteGroups = () => {
		const groupIds = gridRowSelectionIdsArray(selectedGroups, organizationGroups).map(String);
		const deleteData = { groupIds };
		const plural = groupIds.length > 1;
		axios.delete(apiRoutes.bulkDeleteGroups, { headers: authHeader, data: deleteData })
			.then(() => {
				setGroupSeed(Math.random());
				setBulkDeleteDialogOpen(false);
				setSelectedGroups(emptyGridRowSelectionModel());
			})
			.catch((err: AxiosError) => {
				console.error(err);
				handleToastAlert(messageLevels.ERROR, messages.GROUP_DELETION_ERROR_MSG);
				setBulkDeleteDialogOpen(false);
			});
	};


	const initGroupManagement = () => {
		axios.get(apiRoutes.getGroups, { headers: authHeader })
			.then((res) => {
				setOrganizationGroups(res.data);
				setLoading(false);
			})
			.catch((err: AxiosError) => {
				console.log(err);
			});
	};

	useEffect(() => {
		initGroupManagement();
		handleInitializeSortModel();
	}, [groupSeed]);

	const { gridFilterModel, onFilterModelChange, quickFilterSearchProps, apiRef, visibleRowCount, filterControl, filterButtonEl } = useCoAppDataGridFilters(organizationGroups, loading);
	const selectedGroupCount = resolveGridSelectedRowIds(selectedGroups, organizationGroups).length;

	return (
		<>
			<CoAppDatagridHeader
				iconKind="groups"
				searchIsPresent={true}
				{...quickFilterSearchProps}
				searchPlaceholder="Search all columns…"
				filterControl={filterControl}
				title="Groups"
				resourceType="Group"
				addOnClickHandler={handleToggleGroupCreateDialog}
				addIsPresent={true}
				editIsPresent={false}
				deleteIsPresent={false}
			/>
			<CoAppStandardDataGrid
				apiRef={apiRef}
				filterButtonEl={filterButtonEl}
				filterModel={gridFilterModel}
				onFilterModelChange={onFilterModelChange}
				visibleRowCount={visibleRowCount}
				rows={organizationGroups}
				columns={columns}
				pinnedColumns={["actions"]}
				targetResource="groups"
				gridKey="groups"
				selectionModel={selectedGroups}
				setSelectionModel={setSelectedGroups}
				loading={loading}
				sortModel={sortModel}
				handleSortModelChange={handleSortModelChange}
				selectionFooter={
					<SettingsDataGridSelectionRow
						resourceNoun="group"
						visibleCount={visibleRowCount}
						selectedCount={selectedGroupCount}
						loading={loading}
						onClearSelection={() => setSelectedGroups(emptyGridRowSelectionModel())}
						actions={[{
							label: "Delete",
							icon: <Delete sx={destructiveIconSx} />,
							onClick: () => setBulkDeleteDialogOpen(true),
							variant: "destructive",
						}]}
					/>
				}
			/>
			<CoAppCreateGroupOrRoleModal
				dialogOpen={groupCreateDialogOpen}
				changeHandler={handleGroupNameChange}
				confirmClickHandler={createNewGroup}
				cancelClickHandler={handleToggleGroupCreateDialog}
				nameError={groupNameError}
			/>
			<CoAppDestructiveConfirmationModal
				dialogOpen={groupDeleteDialogOpen}
				dialogTitle={`Delete ${groupBeingDeleted?.name || "this"} group?`}
				dialogMessage={messages.DELETION_CONFIRMATION_MSG(groupBeingDeleted?.name || "", "group")}
				confirmClickHandler={deleteGroup}
				cancelClickHandler={handleToggleGroupDeleteDialog}
				actionText="Delete"
			/>
			<CoAppDestructiveConfirmationModal
				dialogOpen={bulkDeleteDialogOpen}
				dialogTitle={`Delete selected ${gridRowSelectionSize(selectedGroups, organizationGroups) > 1 ? "groups" : "group"}?`}
				dialogMessage={"Are you sure you want to bulk delete all the selected groups? This action cannot be undone."}
				confirmClickHandler={bulkDeleteGroups}
				cancelClickHandler={() => setBulkDeleteDialogOpen(false)}
				actionText="Delete"
			/>
		</>
	);
}
